import { useEffect, useState } from 'react'
import {
    MdCheckCircle,
    MdContentCopy,
    MdErrorOutline,
    MdLinkOff,
    MdSave,
    MdSearch,
    MdSend,
    MdSync,
    MdVerified,
    MdVisibility,
    MdVisibilityOff,
} from 'react-icons/md'
import { toast } from 'react-toastify'

import {
    getOrganizations,
    Organization,
} from '@/lib/api/organization/organization-api'
import {
    adoptTemplatesFromMeta,
    AdoptFromMetaResult,
    getWhatsappTemplates,
    WhatsappTemplate,
} from '@/lib/api/messaging/whatsapp-templates-api'
import {
    ConfiguredWhatsappAccount,
    disconnectWhatsappAccount,
    getWhatsappAccount,
    getWhatsappDepartments,
    linkWhatsappPhoneNumber,
    listWhatsappAccounts,
    sendWhatsappTestMessage,
    registerWhatsappPhoneNumber,
    subscribeWhatsappApp,
    subscribeWhatsappAppToWaba,
    saveWhatsappAccount,
    verifyWhatsappAccount,
    WhatsappAccountPhoneNumber,
    WhatsappAccountStatusResult,
    WhatsappAccountVerifyResult,
    WhatsappDepartmentLink,
} from '@/lib/api/master/master-whatsapp-account-api'

/**
 * Conta WhatsApp (WABA) de uma organização.
 *
 * Diferente dos demais campos desta tela, o que se cadastra aqui **não** é
 * credencial de plataforma: cada organização tem a sua conta, com o próprio
 * token. O que é do app (App ID, App Secret, versão da Graph) continua na
 * categoria Meta. Ver `docs/whatsapp-cloud-api-migration.md` (seção 7.1).
 */
export default function WhatsappAccountCard() {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<Organization[]>([])
    const [searching, setSearching] = useState(false)
    const [organization, setOrganization] = useState<Organization | null>(null)
    const [configured, setConfigured] = useState<ConfiguredWhatsappAccount[]>([])
    const organizationId = organization?.id ?? null
    const [wabaId, setWabaId] = useState('')
    const [accessToken, setAccessToken] = useState('')
    const [showToken, setShowToken] = useState(false)

    const [status, setStatus] = useState<WhatsappAccountStatusResult | null>(null)
    const [phoneNumbers, setPhoneNumbers] = useState<WhatsappAccountPhoneNumber[]>([])

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [verifying, setVerifying] = useState(false)
    const [adopting, setAdopting] = useState(false)
    const [adoption, setAdoption] = useState<AdoptFromMetaResult | null>(null)
    const [departments, setDepartments] = useState<WhatsappDepartmentLink[]>([])
    const [linking, setLinking] = useState<number | null>(null)

    const [templates, setTemplates] = useState<WhatsappTemplate[]>([])
    const [testFrom, setTestFrom] = useState('')
    const [testTo, setTestTo] = useState('')
    const [testTemplate, setTestTemplate] = useState('')
    const [testParams, setTestParams] = useState<string[]>([])
    const [testLanguage, setTestLanguage] = useState('en_US')
    const [sendingTest, setSendingTest] = useState(false)
    const [appSubscribed, setAppSubscribed] = useState<boolean | null>(null)
    const [subscribing, setSubscribing] = useState(false)
    const [registering, setRegistering] = useState<string | null>(null)
    const [diagnosis, setDiagnosis] = useState<WhatsappAccountVerifyResult | null>(null)

    // As contas já cadastradas abrem a tela. Sem isto, quem recarrega a página
    // encontra os botões desabilitados sem entender por quê — a seleção da
    // organização se perde e nada indica que já existe conta gravada.
    useEffect(() => {
        let active = true
        listWhatsappAccounts().then(res => {
            if (!active) return
            const list = res.result ?? []
            setConfigured(list)
            // Uma conta só: entra direto nela, que é o caso de hoje.
            if (list.length === 1) {
                setOrganization({
                    id: list[0].organizationId,
                    name: list[0].organizationName,
                } as Organization)
            }
        })
        return () => {
            active = false
        }
    }, [])

    // Busca no servidor, não no cliente: a lista de organizações cresce sem
    // teto e carregá-la inteira deixaria de funcionar em produção.
    useEffect(() => {
        if (organization) return
        let active = true
        const timer = setTimeout(() => {
            setSearching(true)
            getOrganizations(1, 20, query)
                .then(res => {
                    if (!active) return
                    setResults(res.result?.data ?? [])
                })
                .finally(() => {
                    if (active) setSearching(false)
                })
        }, 300)
        return () => {
            active = false
            clearTimeout(timer)
        }
    }, [query, organization])

    useEffect(() => {
        if (!organizationId) return
        let active = true
        setLoading(true)
        setPhoneNumbers([])
        getWhatsappDepartments(organizationId).then(res => {
            if (active) setDepartments(res.result ?? [])
        })
        // Só os já adotados: os demais a Meta recusaria.
        getWhatsappTemplates().then(res => {
            if (!active) return
            setTemplates((res.result ?? []).filter(t => t.approvalStatus === 'APPROVED'))
        })
        getWhatsappAccount(organizationId)
            .then(res => {
                if (!active) return
                const data = res.result ?? null
                setStatus(data)
                setWabaId(data?.wabaId ?? '')
                // O token nunca volta do backend: o campo nasce vazio e, em
                // branco, mantém o que já está gravado.
                setAccessToken('')

                // Conta ativa: carrega os números junto, para o vínculo e o
                // envio de teste já virem prontos. Sem isto, quem abre a tela
                // encontra o formulário incompleto sem nada explicando que
                // falta clicar num botão antes.
                if (data?.status === 'ACTIVE') {
                    verifyWhatsappAccount(organizationId).then(r => {
                        if (!active) return
                        setPhoneNumbers(r.result?.phoneNumbers ?? [])
                        setAppSubscribed(r.result?.appSubscribed ?? null)
                        setDiagnosis(r.result ?? null)
                    })
                }
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [organizationId])

    async function handleSave() {
        if (!organizationId) return
        if (!wabaId.trim()) {
            toast.error('Informe o ID da conta do WhatsApp (WABA).')
            return
        }
        if (!accessToken.trim()) {
            toast.error('Cole o token de acesso gerado no usuário do sistema da Meta.')
            return
        }

        setSaving(true)
        try {
            const res = await saveWhatsappAccount({
                organizationId,
                wabaId: wabaId.trim(),
                accessToken: accessToken.trim(),
            })
            if (res.error) {
                toast.error(res.error)
                return
            }
            toast.success('Conta do WhatsApp salva.')
            setAccessToken('')
            const refreshed = await getWhatsappAccount(organizationId)
            setStatus(refreshed.result ?? null)
        } finally {
            setSaving(false)
        }
    }

    async function handleVerify() {
        if (!organizationId) return
        setVerifying(true)
        try {
            const res = await verifyWhatsappAccount(organizationId)
            if (res.error) {
                toast.error(res.error)
                setPhoneNumbers([])
            } else {
                setPhoneNumbers(res.result?.phoneNumbers ?? [])
                setAppSubscribed(res.result?.appSubscribed ?? null)
                setDiagnosis(res.result ?? null)
                toast.success('Credenciais válidas.')
            }
            const refreshed = await getWhatsappAccount(organizationId)
            setStatus(refreshed.result ?? null)
        } finally {
            setVerifying(false)
        }
    }

    /**
     * Reconhece os templates que já existem na WABA. Nada é criado nem reenviado
     * para aprovação — eles nunca foram do provedor legado.
     */
    async function handleAdoptTemplates() {
        setAdopting(true)
        try {
            const res = await adoptTemplatesFromMeta()
            if (res.error) {
                toast.error(res.error)
                setAdoption(null)
                return
            }
            const data = res.result ?? null
            setAdoption(data)
            toast.success(
                `${data?.adopted ?? 0} template(s) adotado(s), ${data?.alreadyAdopted ?? 0} já estavam.`,
            )
        } finally {
            setAdopting(false)
        }
    }

    /**
     * Liga o número da Meta ao setor. É o segundo interruptor: sem vínculo, o
     * setor segue no provedor legado mesmo com a chave global em `cloud`.
     */
    async function handleLink(departmentId: number, phoneNumberId: string | null) {
        if (!organizationId) return
        setLinking(departmentId)
        try {
            const res = await linkWhatsappPhoneNumber(organizationId, departmentId, phoneNumberId)
            if (res.error) {
                toast.error(res.error)
                return
            }
            setDepartments(prev =>
                prev.map(d =>
                    d.id === departmentId ? { ...d, whatsappPhoneNumberId: phoneNumberId } : d,
                ),
            )
            toast.success(phoneNumberId ? 'Setor vinculado ao número.' : 'Vínculo desfeito.')
        } finally {
            setLinking(null)
        }
    }

    async function handleSubscribeApp() {
        if (!organizationId) return
        setSubscribing(true)
        try {
            const res = await subscribeWhatsappApp(organizationId)
            if (res.error) {
                toast.error(res.error)
                return
            }
            setAppSubscribed((res.result?.subscribedApps ?? []).length > 0)
            toast.success('App inscrito na conta. O envio já deve funcionar.')
        } finally {
            setSubscribing(false)
        }
    }

    /**
     * Registra o número na Cloud API. Só funciona em número livre — já
     * registrado por outro provedor, precisa ser liberado por ele antes.
     */
    async function handleRegisterNumber(phoneNumberId: string, displayNumber: string) {
        if (!organizationId) return

        const pin = window.prompt(
            `PIN de 6 dígitos para ${displayNumber}.\n\n` +
                'É a verificação em duas etapas DESTE número e passará a ser exigida ' +
                'para registrá-lo em qualquer aplicativo. Guarde-o.',
        )
        if (!pin) return

        setRegistering(phoneNumberId)
        try {
            const res = await registerWhatsappPhoneNumber(organizationId, phoneNumberId, pin.trim())
            if (res.error) {
                toast.error(res.error)
                return
            }
            toast.success('Número registrado. O envio por ele já deve funcionar.')
            const r = await verifyWhatsappAccount(organizationId)
            setPhoneNumbers(r.result?.phoneNumbers ?? [])
        } finally {
            setRegistering(null)
        }
    }

    /**
     * Inscreve o app numa WABA informada à mão — usado para a conta de teste da
     * Meta, que é separada da nossa e por isso não entrega status sem isto.
     */
    async function handleSubscribeOtherWaba() {
        if (!organizationId) return
        const wabaId = window.prompt(
            'ID da conta (WABA) para inscrever o app.\n\n' +
                'A conta de teste da Meta é separada da sua — sem inscrever nela, o envio ' +
                'funciona mas nenhum status de entrega volta.',
        )
        if (!wabaId?.trim()) return

        const res = await subscribeWhatsappAppToWaba(organizationId, wabaId.trim())
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success(
            res.result?.appSubscribed
                ? `App inscrito na conta ${wabaId.trim()}. Os status já devem chegar.`
                : 'A Meta aceitou, mas o app ainda não aparece inscrito.',
        )
    }

    async function handleSendTest() {
        if (!organizationId) return
        setSendingTest(true)
        try {
            const res = await sendWhatsappTestMessage(organizationId, {
                phoneNumberId: testFrom,
                to: testTo,
                templateName: testTemplate,
                language:
                    templates.find(t => t.name === testTemplate)?.language ??
                    (testLanguage || 'pt_BR'),
                params: testParams,
            })
            if (res.error) {
                toast.error(res.error)
                return
            }
            toast.success(`Mensagem enviada. ID: ${res.result?.messageId ?? '—'}`)
        } finally {
            setSendingTest(false)
        }
    }

    async function handleDisconnect() {
        if (!organizationId) return
        const res = await disconnectWhatsappAccount(organizationId)
        if (res.error) {
            toast.error(res.error)
            return
        }
        toast.success('Conta desconectada.')
        setStatus({ configured: false })
        setWabaId('')
        setAccessToken('')
        setPhoneNumbers([])
    }

    function copy(value: string) {
        navigator.clipboard
            .writeText(value)
            .then(() => toast.success('Copiado.'))
            .catch(() => toast.error('Não foi possível copiar.'))
    }

    return (
        <div className="card bg-base-100 border border-base-300 mt-6">
            <div className="card-body gap-5">
                <div>
                    <h3 className="font-semibold text-lg">Conta do WhatsApp por organização</h3>
                    <p className="text-sm text-base-content/60 mt-1">
                        A conta (WABA) e o token são <strong>de cada organização</strong>, não da
                        plataforma — por isso ficam no banco, e não nos campos acima. O que é do
                        app (App ID, App Secret, versão da Graph) continua na categoria Meta.
                    </p>
                </div>

                <div className="form-control w-full max-w-md">
                    <label className="label" htmlFor="wa-organization">
                        <span className="label-text font-medium">Organização</span>
                    </label>

                    {!organization && configured.length > 0 && (
                        <div className="mb-3">
                            <div className="text-xs font-medium text-base-content/60 mb-1.5">
                                Contas já cadastradas
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {configured.map(acc => (
                                    <button
                                        key={acc.organizationId}
                                        type="button"
                                        className="btn btn-sm btn-outline gap-2 normal-case"
                                        onClick={() =>
                                            setOrganization({
                                                id: acc.organizationId,
                                                name: acc.organizationName,
                                            } as Organization)
                                        }
                                    >
                                        <span
                                            className={`badge badge-xs ${
                                                acc.status === 'ACTIVE'
                                                    ? 'badge-success'
                                                    : acc.status === 'ERROR'
                                                      ? 'badge-error'
                                                      : 'badge-ghost'
                                            }`}
                                        />
                                        {acc.organizationName}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {organization ? (
                        <div className="flex items-center justify-between gap-2 rounded-lg border border-base-300 px-3 py-2">
                            <div className="min-w-0">
                                <div className="font-medium truncate">
                                    {organization.name}
                                </div>
                                <div className="text-xs text-base-content/60 truncate">
                                    #{organization.id}
                                    {companyNames(organization) &&
                                        ` · ${companyNames(organization)}`}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs"
                                onClick={() => {
                                    setOrganization(null)
                                    setQuery('')
                                    setStatus(null)
                                    setPhoneNumbers([])
                                }}
                            >
                                Trocar
                            </button>
                        </div>
                    ) : (
                        <>
                            <label className="input input-bordered flex items-center gap-2">
                                <MdSearch className="text-base-content/50" />
                                <input
                                    id="wa-organization"
                                    type="text"
                                    className="grow"
                                    placeholder="Busque pelo nome da organização ou da empresa"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    autoComplete="off"
                                />
                                {searching && (
                                    <span className="loading loading-spinner loading-xs" />
                                )}
                            </label>

                            <div
                                className={`mt-2 max-h-56 overflow-y-auto rounded-lg border border-base-300 divide-y divide-base-300 ${
                                    query.trim() ? '' : 'hidden'
                                }`}
                            >
                                {results.length === 0 && !searching && (
                                    <div className="px-3 py-3 text-sm text-base-content/60">
                                        Nenhuma organização encontrada.
                                    </div>
                                )}
                                {results.map(org => (
                                    <button
                                        key={org.id}
                                        type="button"
                                        className="w-full text-left px-3 py-2 hover:bg-base-200"
                                        onClick={() => setOrganization(org)}
                                    >
                                        <div className="font-medium truncate">{org.name}</div>
                                        <div className="text-xs text-base-content/60 truncate">
                                            #{org.id}
                                            {companyNames(org) && ` · ${companyNames(org)}`}
                                        </div>
                                    </button>
                                ))}
                            </div>
                            <div className="label-text-alt text-base-content/50 mt-1.5 block">
                                A busca também casa com o nome das empresas da rede.
                            </div>
                        </>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-base-content/60">
                        <span className="loading loading-spinner loading-sm" />
                        Carregando situação da conta...
                    </div>
                ) : (
                    <StatusBadge status={status} />
                )}

                <div className="form-control w-full max-w-md">
                    <label className="label" htmlFor="wa-waba-id">
                        <span className="label-text font-medium">
                            ID da conta do WhatsApp (WABA)
                        </span>
                    </label>
                    <input
                        id="wa-waba-id"
                        type="text"
                        className="input input-bordered"
                        placeholder="1560780838600050"
                        value={wabaId}
                        onChange={e => setWabaId(e.target.value)}
                    />
                    <div className="label-text-alt text-base-content/50 mt-1.5 block">
                        Está no Gerenciador de Negócios, em Contas do WhatsApp.
                    </div>
                </div>

                <div className="form-control w-full max-w-md">
                    <label className="label" htmlFor="wa-token">
                        <span className="label-text font-medium">Token de acesso</span>
                    </label>
                    <div className="flex gap-2">
                        <input
                            id="wa-token"
                            type={showToken ? 'text' : 'password'}
                            className="input input-bordered flex-1"
                            placeholder={
                                status?.configured
                                    ? '•••••••••• (em branco mantém)'
                                    : 'Cole o token do usuário do sistema'
                            }
                            value={accessToken}
                            onChange={e => setAccessToken(e.target.value)}
                            autoComplete="off"
                        />
                        <button
                            type="button"
                            className="btn btn-ghost btn-square"
                            onClick={() => setShowToken(v => !v)}
                            title={showToken ? 'Ocultar' : 'Mostrar'}
                        >
                            {showToken ? <MdVisibilityOff /> : <MdVisibility />}
                        </button>
                    </div>
                    <div className="label-text-alt text-base-content/50 mt-1.5 block">
                        Gerado no usuário do sistema, com expiração <strong>Nunca</strong> e as
                        permissões <code>whatsapp_business_messaging</code>,{' '}
                        <code>whatsapp_business_management</code> e <code>business_management</code>.
                        É gravado criptografado e nunca volta a ser exibido.
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <button
                        className="btn btn-primary btn-sm gap-1"
                        onClick={handleSave}
                        disabled={saving || !organizationId}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdSave />
                        )}
                        Salvar conta
                    </button>

                    <button
                        className="btn btn-outline btn-sm gap-1"
                        onClick={handleVerify}
                        disabled={verifying || !status?.configured}
                        title="Testa o token na Meta e lista os números da conta"
                    >
                        {verifying ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdVerified />
                        )}
                        Testar e listar números
                    </button>

                    <button
                        className="btn btn-outline btn-sm gap-1"
                        onClick={handleAdoptTemplates}
                        disabled={adopting || status?.status !== 'ACTIVE'}
                        title="Reconhece os templates que já existem na conta da Meta. Não cria nem reaprova nenhum."
                    >
                        {adopting ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdSync />
                        )}
                        Adotar templates da conta
                    </button>

                    <button
                        className="btn btn-ghost btn-sm gap-1"
                        onClick={handleSubscribeOtherWaba}
                        disabled={status?.status !== 'ACTIVE'}
                        title="Inscreve o app em outra conta (ex.: a de teste da Meta), para os status de entrega chegarem"
                    >
                        Inscrever em outra conta
                    </button>

                    {status?.configured && (
                        <button
                            className="btn btn-ghost btn-sm gap-1 text-error"
                            onClick={handleDisconnect}
                            title="Remove o token gravado"
                        >
                            <MdLinkOff />
                            Desconectar
                        </button>
                    )}
                </div>

                {phoneNumbers.length > 0 && (
                    <PhoneNumbersTable
                        numbers={phoneNumbers}
                        onCopy={copy}
                        registering={registering}
                        onRegister={handleRegisterNumber}
                    />
                )}

                {adoption && <AdoptionReport report={adoption} />}

                {diagnosis && <TokenDiagnosis result={diagnosis} />}

                {appSubscribed === false && (
                    <div className="alert alert-warning py-3">
                        <MdErrorOutline />
                        <div className="text-sm">
                            <div className="font-medium">
                                O app não está inscrito nesta conta.
                            </div>
                            <div className="opacity-80 mt-0.5">
                                Ler a conta funciona, mas <strong>enviar não</strong> — a Meta
                                recusa com erro de permissão. Inscrever é o que habilita envio e
                                recebimento.
                            </div>
                        </div>
                        <button
                            className="btn btn-sm"
                            onClick={handleSubscribeApp}
                            disabled={subscribing}
                        >
                            {subscribing ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                'Inscrever app'
                            )}
                        </button>
                    </div>
                )}

                {phoneNumbers.length > 0 && templates.length > 0 && (
                    <TestMessage
                        numbers={phoneNumbers}
                        templates={templates}
                        from={testFrom}
                        to={testTo}
                        templateName={testTemplate}
                        params={testParams}
                        sending={sendingTest}
                        onFrom={setTestFrom}
                        onTo={setTestTo}
                        onTemplate={name => {
                            setTestTemplate(name)
                            const tpl = templates.find(t => t.name === name)
                            setTestParams(
                                Array((tpl?.variables ?? []).length).fill(''),
                            )
                        }}
                        onParam={(i, v) =>
                            setTestParams(prev => prev.map((p, idx) => (idx === i ? v : p)))
                        }
                        onSend={handleSendTest}
                        onRegisterCustom={id =>
                            handleRegisterNumber(id, `número de teste ${id}`)
                        }
                        registeringCustom={registering === testFrom}
                        customLanguage={testLanguage}
                        onLanguage={setTestLanguage}
                    />
                )}

                {departments.length > 0 && (
                    <DepartmentLinks
                        departments={departments}
                        numbers={phoneNumbers}
                        linking={linking}
                        onLink={handleLink}
                    />
                )}
            </div>
        </div>
    )
}

/**
 * Nomes das empresas da organização. É o que resolve a confusão de identidade:
 * o operador conhece a rede pela unidade ("Mediar Saúde"), não pelo nome da
 * organização ("MediarDex System").
 */
function companyNames(org: Organization): string {
    const companies = (org as unknown as { companies?: { name: string }[] }).companies
    if (!companies?.length) return ''
    const names = companies.map(c => c.name)
    return names.length > 3
        ? `${names.slice(0, 3).join(', ')} +${names.length - 3}`
        : names.join(', ')
}

function StatusBadge({ status }: { status: WhatsappAccountStatusResult | null }) {
    if (!status?.configured) {
        return (
            <div className="alert alert-info py-2">
                <span className="text-sm">
                    Nenhuma conta cadastrada para esta organização.
                </span>
            </div>
        )
    }

    if (status.status === 'ERROR') {
        return (
            <div className="alert alert-error py-2">
                <MdErrorOutline />
                <div className="text-sm">
                    <div className="font-medium">A Meta recusou as credenciais.</div>
                    {status.lastError && (
                        <div className="opacity-80 mt-0.5">{status.lastError}</div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="alert alert-success py-2">
            <MdCheckCircle />
            <span className="text-sm">
                Conta ativa — WABA <code>{status.wabaId}</code>
                {status.tokenExpiresAt
                    ? ` · token vence em ${new Date(status.tokenExpiresAt).toLocaleDateString('pt-BR')}`
                    : ' · token sem expiração'}
            </span>
        </div>
    )
}

/**
 * O que o token realmente pode fazer, direto da Meta.
 *
 * Existe porque a mensagem de erro do envio não diz a causa: token sem
 * `whatsapp_business_messaging` lê a conta inteira e falha só ao enviar, com
 * um texto que fala de permissão sobre a conta.
 */
function TokenDiagnosis({ result }: { result: WhatsappAccountVerifyResult }) {
    const scopes = result.tokenScopes ?? []
    const granular = result.tokenGranularScopes ?? []
    const required = [
        'whatsapp_business_messaging',
        'whatsapp_business_management',
        'business_management',
    ]

    return (
        <div className="rounded-lg border border-base-300 p-3">
            <div className="text-sm font-medium mb-2">Diagnóstico do token</div>

            <div className="flex flex-wrap gap-2 mb-2">
                {required.map(scope => {
                    const has = scopes.includes(scope)
                    return (
                        <span
                            key={scope}
                            className={`badge badge-sm gap-1 ${
                                has ? 'badge-success' : 'badge-error'
                            }`}
                            title={has ? 'presente no token' : 'AUSENTE — gere o token de novo'}
                        >
                            {has ? '✓' : '✗'} {scope}
                        </span>
                    )
                })}
                <span
                    className={`badge badge-sm ${
                        result.appSubscribed ? 'badge-success' : 'badge-error'
                    }`}
                    title={`Nosso app: ${result.ourAppId ?? '—'}`}
                >
                    {result.appSubscribed ? '✓' : '✗'} nosso app inscrito na conta
                </span>
            </div>

            <div className="text-xs text-base-content/70 mb-2 space-y-0.5">
                <div>
                    Token: tipo <code>{result.tokenType ?? '—'}</code> · app{' '}
                    <code>{result.tokenAppId ?? '—'}</code>{' '}
                    <span
                        className={`badge badge-xs ${
                            result.tokenAppMatches ? 'badge-success' : 'badge-error'
                        }`}
                    >
                        {result.tokenAppMatches ? 'é o nosso app' : 'NÃO é o nosso app'}
                    </span>
                </div>
                <div>
                    WABA em uso: <code>{result.wabaId}</code>
                </div>
            </div>

            {(result.subscribedApps?.length ?? 0) > 0 && (
                <div className="text-xs text-base-content/60 mb-2">
                    Apps inscritos nesta conta:{' '}
                    {result.subscribedApps!.map(a => `${a.name ?? 'sem nome'} (${a.id})`).join(', ')}
                    {result.ourAppId && ` · o nosso é ${result.ourAppId}`}
                </div>
            )}

            {result.appSubscribed === false && (
                <div className="text-sm text-error mb-2">
                    O <strong>nosso</strong> app não está inscrito nesta conta — há outro app
                    inscrito, mas não o nosso. Ler a conta funciona; enviar, não.
                </div>
            )}

            {result.canSendMessages === false && (
                <div className="text-sm text-error">
                    O token <strong>não tem</strong> <code>whatsapp_business_messaging</code>.
                    Ele lê a conta, mas a Meta recusa qualquer envio. Gere o token novamente no
                    usuário do sistema marcando esse escopo.
                </div>
            )}

            {/*
              O que decide o envio não é o escopo estar na lista geral, e sim
              ele cobrir ESTA conta. Um token pode ter
              `whatsapp_business_messaging` e mesmo assim não valer para esta
              WABA — e o erro que a Meta devolve nesse caso fala de permissão
              sobre a conta, sem mencionar escopo.
            */}
            {granular.length > 0 && (
                <div className="mt-2">
                    <div className="text-xs font-medium mb-1">
                        Escopos por conta (o que realmente vale)
                    </div>
                    <table className="table table-xs">
                        <tbody>
                            {granular.map(g => {
                                const covers =
                                    !g.targetIds?.length || g.targetIds.includes(result.wabaId)
                                return (
                                    <tr key={g.scope}>
                                        <td className="font-mono text-xs">{g.scope}</td>
                                        <td className="text-xs">
                                            {g.targetIds?.length
                                                ? g.targetIds.join(', ')
                                                : 'todas as contas'}
                                        </td>
                                        <td>
                                            <span
                                                className={`badge badge-xs ${
                                                    covers ? 'badge-success' : 'badge-error'
                                                }`}
                                            >
                                                {covers ? 'cobre esta conta' : 'NÃO cobre'}
                                            </span>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {scopes.length > 0 && (
                <details className="text-xs text-base-content/60 mt-2">
                    <summary className="cursor-pointer">Todos os escopos ({scopes.length})</summary>
                    <div className="mt-1 font-mono break-all">{scopes.join(', ')}</div>
                </details>
            )}
        </div>
    )
}

function TestMessage({
    numbers,
    templates,
    from,
    to,
    templateName,
    params,
    sending,
    onFrom,
    onTo,
    onTemplate,
    onParam,
    onSend,
    onRegisterCustom,
    registeringCustom,
    customLanguage,
    onLanguage,
}: {
    numbers: WhatsappAccountPhoneNumber[]
    templates: WhatsappTemplate[]
    from: string
    to: string
    templateName: string
    params: string[]
    sending: boolean
    onFrom: (v: string) => void
    onTo: (v: string) => void
    onTemplate: (v: string) => void
    onParam: (index: number, value: string) => void
    onSend: () => void
    onRegisterCustom: (phoneNumberId: string) => void
    registeringCustom: boolean
    customLanguage: string
    onLanguage: (v: string) => void
}) {
    const selected = templates.find(t => t.name === templateName)
    const ready = from && to.replace(/\D/g, '').length >= 10 && templateName

    return (
        <div className="rounded-lg border border-warning/40 bg-warning/5 p-4">
            <h4 className="font-medium mb-1">Enviar mensagem de teste</h4>
            <p className="text-sm text-base-content/70 mb-4">
                Envia uma mensagem <strong>real</strong> pela API oficial. Usa template
                porque texto livre só é entregue dentro da janela de 24h — num primeiro
                contato a Meta recusaria. O número de teste da Meta é gratuito e vive numa
                conta própria: escolha <em>Outro</em> e cole o ID dele.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
                <div className="form-control">
                    <label className="label" htmlFor="wa-test-from">
                        <span className="label-text font-medium">Enviar de</span>
                    </label>
                    <select
                        id="wa-test-from"
                        className="select select-bordered select-sm"
                        value={numbers.some(n => n.id === from) ? from : '__custom__'}
                        onChange={e =>
                            onFrom(e.target.value === '__custom__' ? '' : e.target.value)
                        }
                    >
                        <option value="">Escolha o número</option>
                        {numbers.map(n => (
                            <option key={n.id} value={n.id}>
                                {n.displayPhoneNumber}
                            </option>
                        ))}
                        <option value="__custom__">Outro (ID de número de teste)</option>
                    </select>
                    {!numbers.some(n => n.id === from) && (
                        <div className="flex gap-2 mt-2">
                            <input
                                type="text"
                                className="input input-bordered input-sm flex-1"
                                placeholder="ID do número de teste (ex.: 1277293255465709)"
                                value={from}
                                onChange={e => onFrom(e.target.value.trim())}
                                autoComplete="off"
                            />
                            {/*
                              O número de teste também precisa ser registrado na
                              Cloud API — sem isso a Meta responde
                              `(#133010) Account not registered`. Como ele não
                              pertence à nossa WABA, não aparece na tabela acima
                              e precisa deste atalho.
                            */}
                            <button
                                type="button"
                                className="btn btn-sm btn-outline"
                                onClick={() => onRegisterCustom(from)}
                                disabled={!from || registeringCustom}
                                title="Registra este número na Cloud API"
                            >
                                {registeringCustom ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    'Registrar'
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <div className="form-control">
                    <label className="label" htmlFor="wa-test-to">
                        <span className="label-text font-medium">Enviar para</span>
                    </label>
                    <input
                        id="wa-test-to"
                        type="tel"
                        className="input input-bordered input-sm"
                        placeholder="77 99999-0000"
                        value={to}
                        onChange={e => onTo(e.target.value)}
                        autoComplete="off"
                    />
                </div>
            </div>

            <div className="form-control mt-3">
                <label className="label" htmlFor="wa-test-template">
                    <span className="label-text font-medium">Template</span>
                </label>
                <select
                    id="wa-test-template"
                    className="select select-bordered select-sm"
                    value={templates.some(t => t.name === templateName) ? templateName : '__custom__'}
                    onChange={e =>
                        onTemplate(e.target.value === '__custom__' ? '' : e.target.value)
                    }
                >
                    <option value="">Escolha o template</option>
                    {templates.map(t => (
                        <option key={t.id} value={t.name}>
                            {t.friendlyName} ({t.name})
                        </option>
                    ))}
                    <option value="__custom__">Outro (informar o nome)</option>
                </select>

                {/*
                  Os templates listados são os da NOSSA conta. O número de teste
                  vive em outra WABA, com os templates dela — e usar um nome que
                  não existe lá devolve
                  `(#132001) template name does not exist in <idioma>`.
                  A conta de teste vem com `hello_world` em `en_US`.
                */}
                {!templates.some(t => t.name === templateName) && (
                    <div className="grid gap-2 sm:grid-cols-2 mt-2">
                        <input
                            type="text"
                            className="input input-bordered input-sm"
                            placeholder="Nome do template (ex.: hello_world)"
                            value={templateName}
                            onChange={e => onTemplate(e.target.value.trim())}
                            autoComplete="off"
                        />
                        <input
                            type="text"
                            className="input input-bordered input-sm"
                            placeholder="Idioma (ex.: en_US)"
                            value={customLanguage}
                            onChange={e => onLanguage(e.target.value.trim())}
                            autoComplete="off"
                        />
                    </div>
                )}
            </div>

            {selected?.bodyText && (
                <div className="mt-3 rounded bg-base-200 p-3 text-sm whitespace-pre-wrap">
                    {selected.bodyText}
                </div>
            )}

            {params.length > 0 && (
                <div className="grid gap-2 sm:grid-cols-2 mt-3">
                    {params.map((value, i) => (
                        <div className="form-control" key={i}>
                            <label className="label py-1" htmlFor={`wa-test-param-${i}`}>
                                <span className="label-text text-xs">
                                    Variável {`{{${i + 1}}}`}
                                </span>
                            </label>
                            <input
                                id={`wa-test-param-${i}`}
                                type="text"
                                className="input input-bordered input-sm"
                                value={value}
                                onChange={e => onParam(i, e.target.value)}
                            />
                        </div>
                    ))}
                </div>
            )}

            <button
                className="btn btn-warning btn-sm gap-1 mt-4"
                onClick={onSend}
                disabled={sending || !ready}
            >
                {sending ? (
                    <span className="loading loading-spinner loading-xs" />
                ) : (
                    <MdSend />
                )}
                Enviar mensagem real
            </button>
        </div>
    )
}

function DepartmentLinks({
    departments,
    numbers,
    linking,
    onLink,
}: {
    departments: WhatsappDepartmentLink[]
    numbers: WhatsappAccountPhoneNumber[]
    linking: number | null
    onLink: (departmentId: number, phoneNumberId: string | null) => void
}) {
    return (
        <div>
            <h4 className="font-medium mb-2">Setores na API oficial</h4>
            <p className="text-sm text-base-content/60 mb-3">
                Vincular o número é o <strong>segundo interruptor</strong> do corte: sem ele, o
                setor continua no provedor antigo mesmo com a chave global ligada. Desfazer o
                vínculo devolve o setor ao caminho antigo, sem deploy.
                {numbers.length === 0 && (
                    <>
                        {' '}
                        Rode <strong>Testar e listar números</strong> antes para carregar os
                        números disponíveis.
                    </>
                )}
            </p>
            <div className="overflow-x-auto">
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>Setor</th>
                            <th>Telefone cadastrado</th>
                            <th>Canal</th>
                            <th>Número da Meta</th>
                        </tr>
                    </thead>
                    <tbody>
                        {departments.map(d => (
                            <tr key={d.id}>
                                <td className="whitespace-nowrap">{d.name}</td>
                                <td className="whitespace-nowrap">{d.whatsappNumber ?? '—'}</td>
                                <td>
                                    <span className="badge badge-ghost badge-sm">
                                        {d.whatsappProvider === 'WHATSAPP_WEB'
                                            ? 'WhatsApp Web'
                                            : 'API oficial'}
                                    </span>
                                </td>
                                <td>
                                    <select
                                        className="select select-bordered select-sm min-w-56"
                                        value={d.whatsappPhoneNumberId ?? ''}
                                        disabled={
                                            linking === d.id ||
                                            numbers.length === 0 ||
                                            d.whatsappProvider === 'WHATSAPP_WEB'
                                        }
                                        onChange={e => onLink(d.id, e.target.value || null)}
                                    >
                                        <option value="">Não vinculado (provedor antigo)</option>
                                        {numbers.map(n => (
                                            <option key={n.id} value={n.id}>
                                                {n.displayPhoneNumber} · {n.id}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

function AdoptionReport({ report }: { report: AdoptFromMetaResult }) {
    return (
        <div>
            <h4 className="font-medium mb-2">Adoção dos templates</h4>
            <div className="flex flex-wrap gap-2 mb-3">
                <span className="badge badge-success gap-1">
                    {report.adopted} adotado(s)
                </span>
                <span className="badge badge-ghost gap-1">
                    {report.alreadyAdopted} já adotado(s)
                </span>
                {report.unmatchedLocal.length > 0 && (
                    <span className="badge badge-warning gap-1">
                        {report.unmatchedLocal.length} sem par na Meta
                    </span>
                )}
                {report.unmatchedRemote.length > 0 && (
                    <span className="badge badge-info gap-1">
                        {report.unmatchedRemote.length} na Meta sem par local
                    </span>
                )}
            </div>

            {report.unmatchedLocal.length > 0 && (
                <div className="mb-3">
                    <div className="text-sm font-medium mb-1">
                        Templates nossos que não existem na conta
                    </div>
                    <ul className="text-sm text-base-content/70 list-disc list-inside">
                        {report.unmatchedLocal.map(t => (
                            <li key={t.id}>
                                {t.name} <span className="opacity-60">({t.language})</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {report.unmatchedRemote.length > 0 && (
                <div>
                    <div className="text-sm font-medium mb-1">
                        Templates na conta sem correspondente aqui
                    </div>
                    <ul className="text-sm text-base-content/70 list-disc list-inside">
                        {report.unmatchedRemote.map(t => (
                            <li key={`${t.name}-${t.language}`}>
                                {t.name}{' '}
                                <span className="opacity-60">
                                    ({t.language} · {t.status})
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

function PhoneNumbersTable({
    numbers,
    onCopy,
    registering,
    onRegister,
}: {
    numbers: WhatsappAccountPhoneNumber[]
    onCopy: (value: string) => void
    registering: string | null
    onRegister: (phoneNumberId: string, displayNumber: string) => void
}) {
    return (
        <div>
            <h4 className="font-medium mb-2">Números da conta</h4>
            <p className="text-sm text-base-content/60 mb-3">
                O <strong>ID do número</strong> é o que identifica o remetente no envio e no
                webhook — é ele que vai no cadastro do setor, não o telefone em texto.
            </p>
            <div className="overflow-x-auto">
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>Número</th>
                            <th>Nome exibido</th>
                            <th>Qualidade</th>
                            <th>ID do número</th>
                            <th>Envio</th>
                        </tr>
                    </thead>
                    <tbody>
                        {numbers.map(n => (
                            <tr key={n.id}>
                                <td className="whitespace-nowrap">{n.displayPhoneNumber}</td>
                                <td>{n.verifiedName ?? '—'}</td>
                                <td>
                                    {n.qualityRating ? (
                                        <span className="badge badge-ghost badge-sm">
                                            {n.qualityRating}
                                        </span>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                                <td>
                                    <button
                                        className="btn btn-ghost btn-xs gap-1 font-mono"
                                        onClick={() => onCopy(n.id)}
                                        title="Copiar o ID do número"
                                    >
                                        {n.id}
                                        <MdContentCopy />
                                    </button>
                                </td>
                                <td>
                                    {/*
                                      O que decide é `status` (situação na Cloud
                                      API), NÃO `codeVerificationStatus` (que é a
                                      verificação por SMS). Confundir os dois põe
                                      o botão no número errado — e registrar o
                                      número produtivo o tomaria do provedor
                                      atual, derrubando o envio em produção.
                                    */}
                                    {n.status === 'CONNECTED' ? (
                                        <span className="badge badge-success badge-sm">
                                            Registrado
                                        </span>
                                    ) : n.status === 'PENDING' ? (
                                        <button
                                            className="btn btn-xs btn-outline"
                                            onClick={() => onRegister(n.id, n.displayPhoneNumber)}
                                            disabled={registering === n.id}
                                            title="Registra o número na API oficial — é o que habilita o envio por ele"
                                        >
                                            {registering === n.id ? (
                                                <span className="loading loading-spinner loading-xs" />
                                            ) : (
                                                'Registrar'
                                            )}
                                        </button>
                                    ) : (
                                        <span
                                            className="badge badge-ghost badge-sm"
                                            title="Só número PENDING pode ser registrado aqui"
                                        >
                                            {n.status ?? '—'}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
